import { STYLES } from './styles.js';
import { Pill } from './pill.js';
import { SelectionWatcher } from './selection.js';
import { Player } from './player.js';
import { SettingsPanel } from './settings.js';
import { SelectionBubble } from './bubble.js';
import { InPageHighlighter } from './karaoke.js';
import { makeTranslator, type TranslateFn } from './i18n.js';
import { extractPageContext, defaultPageContextEnabled, type PageContext } from './pageContext.js';

const KEY_PATTERN = /^pk_(live|test)_[A-Za-z0-9]{16,64}$/;
const VALID_POSITIONS = ['bottom-right', 'bottom-left', 'top-right', 'top-left'] as const;
type Position = (typeof VALID_POSITIONS)[number];

type Config = {
  apiKey: string;
  apiBase: string;
  voice: string;
  position: Position;
};

function readConfig(): Config | null {
  const script = (document.currentScript ?? findScript()) as HTMLScriptElement | null;
  if (!script) {
    console.warn('[llais] cannot locate widget script tag');
    return null;
  }
  const apiKey = (script.dataset.key ?? '').trim();
  if (!KEY_PATTERN.test(apiKey)) {
    console.warn('[llais] invalid or missing data-key');
    return null;
  }
  const positionRaw = (script.dataset.position ?? 'bottom-right') as Position;
  const position = (VALID_POSITIONS as readonly string[]).includes(positionRaw)
    ? positionRaw
    : 'bottom-right';

  return {
    apiKey,
    apiBase: (script.dataset.api ?? 'https://llais.capsiynau.com').replace(/\/+$/, ''),
    voice: script.dataset.voice ?? 'nia',
    position,
  };
}

function findScript(): HTMLScriptElement | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[data-key]');
  for (const s of scripts) {
    if (s.src.includes('widget.v1')) return s;
  }
  return scripts[scripts.length - 1] ?? null;
}

function mount(cfg: Config): void {
  const host = document.createElement('div');
  host.id = 'llais-widget-host';
  host.style.cssText = 'all:initial;';
  document.documentElement.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = STYLES;
  shadow.appendChild(style);

  const root = document.createElement('div');
  root.className = `root ${cfg.position}`;
  shadow.appendChild(root);

  const showToast = (msg: string, kind: 'info' | 'error' = 'info'): void => {
    const t = document.createElement('div');
    t.className = `toast${kind === 'error' ? ' error' : ''}`;
    t.textContent = msg;
    t.setAttribute('role', 'status');
    root.appendChild(t);
    setTimeout(() => t.remove(), 3500);
  };

  // Translator — refreshed whenever settings.uiLang changes (the same
  // function reference is replaced; everything that reads strings goes
  // through tr() at call time, not capture time).
  let tr: TranslateFn = makeTranslator('en');

  const refreshPillIdle = (): void => {
    // If the pill is currently in the idle-but-active state, refresh
    // its status label to match the new language.
    if (pill && pill.isActive) {
      pill.setState('idle');
    }
  };

  // Page context is captured once at mount. SPAs that re-render the title
  // mid-session keep the original snapshot - good enough for Phase A and
  // matches how detectInitialLang() already behaves.
  const pageContext: PageContext = extractPageContext();

  // Settings panel — owns voice + speed + translate state, persists to localStorage.
  const settings = new SettingsPanel({
    apiBase: cfg.apiBase,
    defaultVoice: cfg.voice,
    defaultPageContextEnabled: defaultPageContextEnabled(cfg.apiKey),
    onChange: (s) => {
      // Refresh translator if the language changed.
      tr = makeTranslator(s.uiLang);
      settings.refreshLabels(tr);
      bubble.refreshLabels(tr);
      refreshPillIdle();
      // Live-update playbackRate so a mid-listen speed change takes effect.
      player.setSpeed(s.speed);
    },
    onClose: () => {
      /* close handled by panel internally */
    },
    initialTranslator: tr,
  });
  root.appendChild(settings.root);

  // After SettingsPanel reads its initial settings from localStorage, sync
  // the translator to whatever uiLang the user had saved.
  tr = makeTranslator(settings.value.uiLang);
  settings.refreshLabels(tr);

  // Inline indicator that floats next to the user's selection during the
  // silent gap between mouseup and audio.
  const bubble = new SelectionBubble(tr);
  root.appendChild(bubble.root);

  // In-page word highlighter — wraps the user's selection in spans and
  // lights up the active word in sync with audio. Replaces the previous
  // corner panel: users found that distracting because their eye had to
  // jump from the selection to the corner. Now the highlight stays on
  // the reading line.
  const highlighter = new InPageHighlighter();

  // Player — headless audio element; the pill reflects state.
  const player = new Player({
    apiBase: cfg.apiBase,
    apiKey: cfg.apiKey,
    getSettings: () => settings.value,
    getPageContext: () => (settings.value.pageContextEnabled ? pageContext : null),
    onStateChange: (state) => {
      if (state === 'loading') {
        const isTranslating = settings.value.translate;
        const label = isTranslating ? tr('pillLoadingTranslate') : tr('pillLoadingSpeak');
        pill.setState('loading', { label, translating: isTranslating });
        // Tear down any still-active karaoke FIRST so its restoreFn runs (and
        // is nulled) while its spans are still attached. bubble.show() below
        // clears the .bubble-reading slot; doing that before the highlighter
        // released its restore would leave a stale restoreFn that re-appends
        // the previous translated text on the next highlighter.show(). No-op
        // when nothing is active. (Codex #56)
        highlighter.hide();
        bubble.show(label);
      } else if (state === 'playing') {
        pill.setState('playing', { label: tr('pillPlaying') });
        // Don't bubble.hide() here — onWords (which fires around the
        // same time, when the audio response lands) decides between
        // hiding the loading chip (untranslated path) and morphing it
        // into reading-mode for translated audio.
      } else {
        pill.setState('idle', { label: tr('pillIdle') });
        bubble.hide();
        highlighter.hide();
      }
    },
    onWords: (words, range, translatedText) => {
      if (!words || words.length === 0) {
        bubble.hide();
        highlighter.hide();
        return;
      }
      if (translatedText && translatedText.trim().length > 0) {
        // Translated audio. The page selection is in the source
        // language; the audio is in the target language. Render the
        // translated text in the bubble and run karaoke inside the
        // bubble so the active-word highlight matches the audio.
        const bubbleRange = bubble.showReading(translatedText);
        if (bubbleRange) {
          highlighter.show(bubbleRange, words);
        } else {
          highlighter.hide();
        }
      } else if (range) {
        // Untranslated audio. Page text matches audio words — wrap
        // them in-place as before. The loading chip can go.
        bubble.hide();
        highlighter.show(range, words);
      } else {
        bubble.hide();
        highlighter.hide();
      }
    },
    onTimeUpdate: (currentTimeS) => {
      highlighter.update(currentTimeS);
    },
    onToast: showToast,
  });
  root.appendChild(player.element);

  // Selection watcher — auto-fires Player.load when pill is active.
  const selection = new SelectionWatcher((text, rect, range) => {
    bubble.positionTo(rect);
    void player.load(text, range);
  });

  // Pill — the one visible surface. Owns the on/off gate.
  const pill = new Pill({
    onToggle: (active) => {
      if (active) {
        selection.enable();
        pill.setState('idle', { label: tr('pillIdle') });
      } else {
        selection.disable();
        settings.close();
        player.stop();
        highlighter.hide();
      }
    },
    onSettings: () => {
      settings.toggle();
    },
    initialTranslator: tr,
  });
  root.appendChild(pill.root);

  // Click-outside dismisses settings.
  document.addEventListener('click', (e) => {
    const target = e.target as Node;
    if (host.contains(target)) return;
    settings.close();
  });

  pill.applyInitial();
}

function boot(): void {
  const cfg = readConfig();
  if (!cfg) return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(cfg), { once: true });
  } else {
    mount(cfg);
  }
}

boot();
